"use client";

import { useState } from "react";
import { Product, PaginatedProductResponse } from "@/types/product.types";
import { ProductCard } from "@/components/shared/ProductCard";
import { CatalogPagination } from "./CatalogPagination";
import { useRouter, useSearchParams } from "next/navigation";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbPage,
    BreadcrumbSeparator,
    BreadcrumbList,
} from "@/components/ui/breadcrumb";
import { QuickViewModal } from "@/components/shared/model/QuickViewModal";

interface Props {
    initialData: PaginatedProductResponse;
    currentPage: number;
}

export const PacksCatalog = ({ initialData, currentPage }: Props) => {
    const router = useRouter();
    const searchParams = useSearchParams();

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
    const [isQuickViewOpen, setIsQuickViewOpen] = useState(false);

    const products = initialData.data?.data || [];
    const totalPages = initialData.data?.meta?.totalPages || 1;

    const handlePageChange = (page: number) => {
        const params = new URLSearchParams(searchParams.toString());
        params.set("page", page.toString());
        router.push(`/nos-packs?${params.toString()}`);
    };

    return (
        <section className="bg-primary-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Breadcrumb */}
                <Breadcrumb>
                    <BreadcrumbList>
                        <BreadcrumbItem>
                            <BreadcrumbLink href="/" className="text-primary-500">
                                Accueil
                            </BreadcrumbLink>
                        </BreadcrumbItem>
                        <BreadcrumbSeparator />
                        <BreadcrumbItem>
                            <BreadcrumbPage className="text-primary-500">
                                Nos Packs
                            </BreadcrumbPage>
                        </BreadcrumbItem>
                    </BreadcrumbList>
                </Breadcrumb>

                {/* Title */}
                <h1 className="font-brastika text-3xl sm:text-4xl lg:text-5xl mt-6 mb-10 text-primary-900">
                    Nos Packs Épices & Bien-être
                </h1>

                {/* Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                    {products.map((product: Product) => (
                        <ProductCard 
                            key={product.id} 
                            product={product} 
                            onQuickView={() => { 
                                setSelectedProduct(product);
                                setIsQuickViewOpen(true);
                            }} 
                        />
                    ))}
                </div>

                {/* Empty State */}
                {products.length === 0 && (
                    <div className="text-center py-20">
                        <p className="text-xl text-primary-600">Aucun pack disponible pour le moment.</p>
                    </div>
                )}

                {/* Pagination */}
                <div className="mt-12 flex justify-center">
                    <CatalogPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </div>
            </div>

            <QuickViewModal
                product={selectedProduct}
                isOpen={isQuickViewOpen}
                onClose={() => setIsQuickViewOpen(false)}
            />
        </section>
    );
};
